import { z } from "zod";
import { IsoDateSchema, IsoDateTimeSchema, MoneySchema } from "./primitives.js";
/**
 * Retailer weekly offers (ADR-028 Phase 1 contract surface).
 *
 * Provenance rules (C-043 / spec rule 2): every offer carries retailer,
 * validity window, capture timestamp, and a Money whose status is `cached`
 * (a real captured price served from our store — never `estimated`, never
 * invented). `fetched_for_zip` is deliberately NOT exposed on the wire: it is
 * fetch provenance of the representative-zip refresh, not user data, and
 * offers are never user-location-scoped. The mandatory "may vary by store"
 * label is UI copy (i18n), not a wire field.
 */
/**
 * Exact vocabulary stored in profiles.budget.preferredRetailers
 * (Step11Budget) and CHECK-pinned on the offers table (migration 0025).
 */
export const OfferRetailerSchema = z.enum(["REWE", "EDEKA", "Lidl", "Aldi", "Kaufland", "Penny"]);
/** Pack/unit info where the source provides it — never derived or guessed. */
export const OfferUnitInfoSchema = z.object({
    unitAmount: z.number().positive().describe("Pack size in `unit`, e.g. 250 (g)."),
    unit: z.string().describe("Source-provided unit, e.g. 'g', 'ml', 'Stück'."),
    pricePerUnitCents: z
        .number()
        .int()
        .nonnegative()
        .nullable()
        .describe("Normalized comparison price in cents per kg/l/item; null when not derivable."),
    pricePerUnitBasis: z
        .string()
        .nullable()
        .describe("Unit of the normalized comparison price, e.g. 'kg', 'l', 'count'."),
});
export const OfferSchema = z.object({
    id: z.string(),
    retailer: OfferRetailerSchema,
    title: z.string().describe("Raw offer title as published by the retailer (title_raw)."),
    price: MoneySchema.describe("Captured offer price; status is 'cached' for stored offers."),
    unitInfo: OfferUnitInfoSchema.optional(),
    validFrom: IsoDateSchema,
    validTo: IsoDateSchema,
    source: z.string().describe("Adapter/source id, e.g. 'marktguru'."),
    capturedAt: IsoDateTimeSchema.describe("When the refresh run captured this offer."),
    matchedIngredientId: z
        .string()
        .nullable()
        .describe("Deterministic curated match; null never reaches user surfaces (fail-closed)."),
});
/** `plan`: offers matched to the current plan's ingredients; `list`: to the shopping list. */
export const OffersScopeSchema = z.enum(["plan", "list"]);
// GET /v1/offers?scope=plan|list response. An empty `offers` array is the
// honest degraded/disabled state (flag off, no matches, all expired).
export const OffersResponseSchema = z.object({
    offers: z.array(OfferSchema),
    scope: OffersScopeSchema,
});
//# sourceMappingURL=offers.js.map